import { LoadingButton } from '@mui/lab';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { authHeader, handleResponse } from '../queryClient';
import { ApiInstructorExercise, ApiInstructorExerciseRequest } from '../types';
import { useActiveCompany } from '../utils/useCompany';

export interface EditTitleDialogProps {
  id: string;
  onClose: () => void;
  open: boolean;
  title: string;
}

interface TitleFormData {
  title: string;
}

const EditTitleDialog = ({ onClose, open, id, title }: EditTitleDialogProps) => {
  const { control, formState, handleSubmit, setError } = useForm<TitleFormData>({
    defaultValues: { title },
  });

  const queryClient = useQueryClient();
  const activeCompany = useActiveCompany();
  const path = `teach/companies/${activeCompany?.companyId}/scheduled-exercises/${id}`;

  const queryKey = [{ path }];

  const { isLoading, mutate: saveTitle } = useMutation<
    { exerciseId: string },
    Response,
    string,
    { previousExercise: ApiInstructorExercise }
  >({
    async mutationFn(title) {
      const body: Partial<ApiInstructorExerciseRequest> = { title };

      return fetch(`/api/${path}`, {
        body: JSON.stringify(body),
        headers: { ...(await authHeader()), 'Content-Type': 'application/json' },
        method: 'PATCH',
      }).then(handleResponse);
    },
    async onMutate(title) {
      const previousExercise = queryClient.getQueryData<ApiInstructorExercise>(queryKey)!;

      queryClient.setQueryData(queryKey, { ...previousExercise, title });

      return { previousExercise };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });

      onClose();
    },
    onError: async (error, title, context) => {
      if (context) {
        queryClient.setQueryData(queryKey, context.previousExercise);
      }

      const body = await error.json();

      setError('title', { message: body.title });
    },
  });

  const onSubmit: SubmitHandler<TitleFormData> = ({ title }, e) => {
    e?.preventDefault();

    saveTitle(title);
  };

  return (
    <Dialog fullWidth open={open} onClose={onClose}>
      <form onSubmit={handleSubmit(onSubmit)} noValidate autoComplete="off">
        <DialogTitle>Exercise&rsquo;s title</DialogTitle>
        <DialogContent>
          <Controller
            control={control}
            name="title"
            render={({ field }) => (
              <TextField
                {...field}
                error={!!formState.errors.title}
                fullWidth
                helperText={formState.errors.title?.message}
                required
                inputProps={{ maxLength: 100 }}
                label="Title"
                sx={{ mt: 1 }}
              />
            )}
            rules={{ required: 'Please fill in a title' }}
          />
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between' }}>
          <Button onClick={onClose}>Cancel</Button>
          <LoadingButton loading={isLoading} type="submit" variant="contained">
            Save
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EditTitleDialog;
